/**
 * Note visibility rules (W1-06; R§4.5 in order, R§4.4, F§10.2, F§11.3, A§7.5).
 *
 * Order of evaluation (each rule hides the note when it applies):
 *   1. the line is a UI mover (attachUI): neither the line nor its notes are drawn;
 *   2. line alpha < 0 (strictly, LineStateRt.alpha from the f32 path): no notes of the line are drawn;
 *   3. judged non-hold: autoplay judges a real non-hold on the first frame with t >= time (it vanishes exactly at
 *      its time); judged play asks the JudgeView; fake notes are never judged;
 *   4. a hold with t >= endTime;
 *   5. cover rules on `isCover == 1` lines (draw_below false): a non-hold past `time + 0.16` or a fake non-hold at
 *      or after its time, and any note before its time whose cover base is <= -0.001 (holds use the end position
 *      with holdPartialCover);
 *   6. the off-screen quad cull (geometry.ts);
 *   7. aggressive culling (culling.ts).
 * Preview filters (hidden / solo lines) are editor-only and come first; they never reach an export.
 */

import type { LineId } from '../../model/types';
import type { GhostReason, JudgeView, LineStateRt, PreviewFilters } from '../types';
import { COVER_THRESHOLD, FADEOUT_TIME } from './constants';
import { clamp01 } from './geometry';
import type { LineNotesRt } from './runtime';
import { NOTE_HOLD } from './runtime';

/** Whether the preview filters hide `line` (hidden, or not soloed while a solo set exists). */
export function lineFilteredOut(filters: PreviewFilters, line: LineId): boolean {
  if (filters.hiddenLines.has(line)) return true;
  return filters.soloLines.size > 0 && !filters.soloLines.has(line);
}

/** Preview dim multiplier of a line: 1 - factor for every line except the active one (VIEW-03). */
export function dimMultiplier(filters: PreviewFilters | null, line: LineId): number {
  const dim = filters?.dim ?? null;
  if (dim === null || dim.activeLine === line) return 1;
  return Math.min(1, Math.max(0, 1 - dim.factor));
}

/**
 * Line-level reason why no note of the line is drawn (rules 1-2 plus preview filters), or null.
 * Filters come first: they are what hides the line in the editor preview.
 */
export function lineNotesHiddenReason(
  state: Pick<LineStateRt, 'line' | 'uiMover' | 'alpha'>,
  filters: PreviewFilters | null,
): GhostReason | null {
  if (filters !== null && lineFilteredOut(filters, state.line)) return 'filtered';
  if (state.uiMover) return 'uiMoverLine';
  if (state.alpha < 0) return 'negativeLineAlpha';
  return null;
}

/**
 * Rule 3: whether note `i` counts as judged at `t`. Autoplay: a real note with t >= time. Judged play: the
 * JudgeView has a judgement made at or before t. Fake notes are never judged.
 */
export function isJudged(rt: LineNotesRt, i: number, t: number, judge: JudgeView | null): boolean {
  if (rt.fake[i] === 1) return false;
  if (judge === null) return t >= (rt.time[i] ?? Infinity);
  const id = rt.id[i] ?? -1;
  if (id < 0) return t >= (rt.time[i] ?? Infinity);
  const s = judge.status(rt.noteId(i));
  return s !== null && s.at <= t;
}

/** Judged play: the note's judgement is a miss (holds are then drawn at half alpha, R§4.4). */
export function isMissed(rt: LineNotesRt, i: number, t: number, judge: JudgeView | null): boolean {
  if (judge === null || rt.fake[i] === 1) return false;
  const id = rt.id[i] ?? -1;
  if (id < 0) return false;
  const s = judge.status(rt.noteId(i));
  return s !== null && s.at <= t && s.grade === 'miss';
}

/** Rules 3-4 for the draw loop: the note is gone (judged non-hold, or hold past its end). */
export function isGone(rt: LineNotesRt, i: number, t: number, judge: JudgeView | null): boolean {
  if (rt.type[i] === NOTE_HOLD) return t >= (rt.endTime[i] ?? -Infinity);
  return isJudged(rt, i, t, judge);
}

/**
 * Rule 5 (cover), for `drawBelow == false` lines only. `coverBase` is the note's base distance (holds: end
 * distance with holdPartialCover).
 */
export function coverHides(rt: LineNotesRt, i: number, t: number, coverBase: number): boolean {
  const time = rt.time[i] ?? 0;
  if (rt.type[i] !== NOTE_HOLD && (t - FADEOUT_TIME >= time || (rt.fake[i] === 1 && t >= time))) return true;
  return time > t && coverBase <= COVER_THRESHOLD;
}

/** Note alpha at t: the visibleTime step (0 before `visibleFrom`, then min(alpha, 255)/255). */
export function noteAlphaAt(rt: LineNotesRt, i: number, t: number): number {
  return t < (rt.visibleFrom[i] ?? -Infinity) ? 0 : (rt.alpha[i] ?? 0);
}

/**
 * Cover-line fade of a non-hold (R§4.4): fake notes at or after their time get 0, others fade linearly over
 * 0.16 s after their time (only reachable before judgement, i.e. in judged play or for fakes).
 */
export function coverFade(rt: LineNotesRt, i: number, t: number): number {
  const time = rt.time[i] ?? 0;
  if (rt.fake[i] === 1 && t >= time) return 0;
  return clamp01(1 + Math.min(time - t, 0) / FADEOUT_TIME);
}
